<script lang="ts" setup>
import { computed } from 'vue'
import IconVirtualTour from '@lahaus-roomie/roomie/src/assets/icons/virtual-tour.svg'
import IconPlay from '@lahaus-roomie/roomie/src/assets/icons/play.svg'

import PdMultimediaContentCard from './Card/index.vue'
import type { Props } from './types'

const props = defineProps<Props>()
const emit = defineEmits(['openGallery'])

const { t } = useI18n()

const images = computed(() => {
  return props.listing.pictures_v2?.filter(image => image.type === 'gallery').sort((a, b) => a.order - b.order).map(image => `${image.url}?auto=compress,enhance,format&w=100&h=100`)
})

const videos = computed(() => {
  const list = props.listing.raw_listing?.attributes?.videos
  if (!list || list?.length === 0) return undefined
  else return list
})

const tours = computed(() => {
  const list = props.listing.raw_listing?.attributes?.tour
  if (!list || list?.length === 0) return undefined
  else return list
})

const webinars = computed(() => {
  const list = props.listing.ims?.webinars_urls
  if (!list || list?.length === 0) return undefined
  else return list
})

const isMediaContentAvailable = computed(() => {
  return !!videos.value || !!tours.value || !!webinars.value
})

</script>

<script lang="ts">
export default {
  name: 'PdMultimediaContent'
}
</script>

<template>
  <section
    class="pd-multimedia-content flex flex-col justify-between">
    <h3
      v-if="isMediaContentAvailable"
      class="text-22 font-medium mb-12 lg:text-32">
      {{ t('title') }}
    </h3>

    <div class="pd-multimedia-content__cards-holder">
      <PdMultimediaContentCard
        v-if="tours"
        :alt="t('alt')"
        :icon="IconVirtualTour"
        icon-class="fill-carbon-600 w-24 h-24"
        :img-src="images[0]"
        :cta-text="t('tour.cta')"
        data-lh-id="pdp-multimedia-content-see-virtual-tour-card"
        @click="emit('openGallery', 'tours')" />

      <PdMultimediaContentCard
        v-if="videos"
        :alt="t('alt')"
        :icon="IconPlay"
        icon-class="fill-primary-600 w-18 h-20"
        :img-src="images[1]"
        :cta-text="t('video.cta')"
        data-lh-id="pdp-multimedia-content-see-video-card"
        @click="emit('openGallery', 'videos')" />

      <PdMultimediaContentCard
        v-if="webinars"
        :alt="t('alt')"
        :icon="IconPlay"
        icon-class="fill-primary-600 w-18 h-20"
        :img-src="images[2]"
        :cta-text="t('webinar.cta')"
        data-lh-id="pdp-multimedia-content-see-webinar-tour-card"
        @click="emit('openGallery', 'webinars')" />
    </div>
  </section>
</template>

<style lang="scss">
.pd-multimedia-content {
  &__cards-holder {
    @apply flex flex-col gap-16;
  }
}

.play-icon-class {
  @apply fill-primary-600;

  & span {
    @apply w-20 h-20;

    & svg {
      @apply w-20 h-20;
    }
  }
}
</style>

<i18n src="./i18n.json"></i18n>
